import { AppModal, Button, Input, Label, Textarea } from "@/components/ui";
import { useCreateManualPhaseAction } from "@/lib/data/react-query/matters/matter-phase";
import type { PhaseActionDefinition } from "@/lib/types";
import { useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";

export function ManualActionEditorModal({
  open,
  onOpenChange,
  onCreated,
  stacked = false,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated?: (action: PhaseActionDefinition) => void;
  stacked?: boolean;
}) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const createAction = useCreateManualPhaseAction();
  const submittingRef = useRef(false);

  useEffect(() => {
    if (!open) return;
    setTitle("");
    setDescription("");
  }, [open]);

  const submit = async () => {
    if (submittingRef.current) return;
    if (!title.trim()) {
      toast.error("Action title is required.");
      return;
    }
    submittingRef.current = true;
    try {
      const response = await createAction.mutateAsync({
        title: title.trim(),
        description: description.trim() || null,
      });
      onCreated?.(response.data);
      onOpenChange(false);
    } finally {
      submittingRef.current = false;
    }
  };

  return (
    <AppModal
      open={open}
      onOpenChange={onOpenChange}
      title="Create custom action"
      description="Custom actions are checked directly on a matter."
      size="lg"
      loading={createAction.isPending}
      isDirty={Boolean(title || description)}
      overlayClassName={stacked ? "z-[108]" : undefined}
      className={stacked ? "z-[109]" : undefined}
      footer={
        <div className="flex w-full justify-end gap-2">
          <Button
            variant="outline"
            onClick={() => {
              onOpenChange(false);
            }}
          >
            Cancel
          </Button>
          <Button
            disabled={createAction.isPending}
            onClick={() => void submit()}
          >
            Create custom action
          </Button>
        </div>
      }
    >
      <fieldset disabled={createAction.isPending} className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="manual-action-title">Title</Label>
          <Input
            id="manual-action-title"
            value={title}
            maxLength={200}
            onChange={(event) => {
              setTitle(event.target.value);
            }}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="manual-action-description">Description</Label>
          <Textarea
            id="manual-action-description"
            value={description}
            maxLength={1000}
            rows={4}
            onChange={(event) => {
              setDescription(event.target.value);
            }}
          />
        </div>
      </fieldset>
    </AppModal>
  );
}
